import { useEffect, useRef, useState } from "react";
import { Mail, PenLine, X, CheckCircle2 } from "lucide-react";

/**
 * Full-screen celebration shown after a successful Gmail OAuth callback.
 *
 * - A single burst of Gmail-red + gold confetti pieces from the top center.
 * - A card slides up with success copy and two CTAs.
 *
 * Props:
 *  - open (bool)
 *  - accountEmail (str)
 *  - onSetupSignature ()  primary CTA
 *  - onDismiss ()         secondary CTA / close
 */
export default function GmailConnectedCelebration({ open, accountEmail, onSetupSignature, onDismiss }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Trigger card entrance one tick after mount for the CSS transition
    const t = setTimeout(() => setVisible(true), 30);
    return () => { clearTimeout(t); setVisible(false); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Rectangle confetti pieces — gmail red, gold, lime + a bit of navy for contrast.
    const COLORS = ["#EA4335", "#FBBC05", "#34A853", "#4285F4", "#7c5cff"];
    const COUNT = 220;
    const pieces = [];
    for (let i = 0; i < COUNT; i++) {
      const originX = w * (0.15 + Math.random() * 0.7); // spread from top width
      pieces.push({
        x: originX,
        y: -20 - Math.random() * 80,
        vx: (Math.random() - 0.5) * 6,          // horizontal drift
        vy: 3 + Math.random() * 5,              // downward speed
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.3,
        w: 6 + Math.random() * 6,
        h: 10 + Math.random() * 8,
        color: COLORS[i % COLORS.length],
        delay: Math.random() * 500,             // stagger release
      });
    }

    const duration = 3200;

    const render = (now) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = now - startRef.current;
      const progress = Math.min(1, elapsed / duration);

      ctx.clearRect(0, 0, w, h);

      // Fade at the end
      let alpha = 1;
      if (progress > 0.75) alpha = Math.max(0, 1 - (progress - 0.75) / 0.25);

      for (const p of pieces) {
        if (elapsed < p.delay) continue;
        const t = (elapsed - p.delay) / 1000;
        // physics: gravity pulls vy up over time
        p.vy += 0.08;
        p.x += p.vx * 0.6;
        p.y += p.vy * 0.9;
        p.rot += p.vr;
        // gentle horizontal wobble
        p.x += Math.sin(t * 3 + p.rot) * 0.3;
        if (p.y > h + 40) continue;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(render);
      }
    };

    rafRef.current = requestAnimationFrame(render);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(15, 18, 28, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: "gmail-celeb-fade 220ms ease-out",
      }}
      data-testid="gmail-connected-celebration"
      onClick={onDismiss}
    >
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: "var(--pg-surface, #ffffff)",
          borderRadius: 20,
          padding: "32px 32px 28px",
          maxWidth: 460,
          width: "calc(100% - 32px)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.08)",
          border: "1px solid var(--pg-border)",
          transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.98)",
          opacity: visible ? 1 : 0,
          transition: "transform 320ms cubic-bezier(0.2, 0.9, 0.3, 1.2), opacity 220ms ease-out",
        }}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          data-testid="gmail-celeb-close"
          style={{
            position: "absolute", top: 14, right: 14, background: "transparent",
            border: "none", cursor: "pointer", padding: 4, color: "var(--pg-text-mute)",
          }}
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: "linear-gradient(135deg, rgba(234,67,53,0.14), rgba(251,188,5,0.14))",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 18px",
          position: "relative",
        }}>
          <Mail size={30} color="#EA4335" strokeWidth={2.2} />
          <div style={{
            position: "absolute", right: -6, bottom: -6,
            width: 26, height: 26, borderRadius: 999,
            background: "#34A853", color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(52,168,83,0.35)",
          }}>
            <CheckCircle2 size={16} strokeWidth={2.4} />
          </div>
        </div>

        <h2 style={{
          fontSize: 26, lineHeight: 1.2, margin: 0, textAlign: "center",
          letterSpacing: "-0.01em", fontWeight: 700,
        }}>
          Your Gmail is connected!
        </h2>
        <p style={{
          textAlign: "center", margin: "10px 0 24px",
          color: "var(--pg-text-mute)", fontSize: 15, lineHeight: 1.5,
        }} data-testid="gmail-celeb-email">
          {accountEmail
            ? <>Connected as <strong style={{ color: "var(--pg-text)" }}>{accountEmail}</strong>. We&apos;ll start pulling emails into your CRM timeline shortly.</>
            : <>We&apos;ll start pulling emails into your CRM timeline shortly.</>}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            className="pg-btn pg-btn-primary"
            onClick={onSetupSignature}
            data-testid="gmail-celeb-setup-signature"
            style={{ justifyContent: "center", padding: "12px 20px", fontSize: 15 }}
          >
            <PenLine size={16} strokeWidth={2.2} />
            Set up your email signature
          </button>
          <button
            type="button"
            className="pg-btn"
            onClick={onDismiss}
            data-testid="gmail-celeb-dismiss"
            style={{ justifyContent: "center", padding: "10px 20px", fontSize: 14, color: "var(--pg-text-mute)" }}
          >
            Not now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes gmail-celeb-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
